import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { BaseUrl } from "../config/api";
import "../assets/styles/CategoryProducts.css";
import "../assets/styles/NewCollections.css";
import { isNewProduct } from "../utils/dateUtils";
import { toHttps } from "../utils/imageUtils";

const NewCollections = () => {
  const [newProducts, setNewProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceRange, setPriceRange] = useState({ min: 1000, max: 50000 });
  const [openFilter, setOpenFilter] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    materialAndCare: [],
    color: [],
    size: [],
    categoryName: [],
    priceRange: { min: 0, max: 1000 },
    discount: [],
  });
  const [sortBy, setSortBy] = useState("relevance");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BaseUrl}/products/getallproducts`);
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }
        const data = await response.json();

        // Filter for products created in the last month
        const transformedProducts = data
          .filter(
            (product) => !product.isDeleted && isNewProduct(product.createdAt)
          )
          .map((product) => ({
            id: product._id,
            name: product.productName,
            price: `Rs. ${product.sellingPrice.toLocaleString()}`,
            images:
              product.images && product.images.length > 0
                ? product.images.map(toHttps)
                : [toHttps(product.categoryId?.categotyImage)],
            fabric: product.materialAndCare || "Not specified",
            color: product.color || "Not specified",
            size: product.sizeAndFit || "Not specified",
            type: product.categoryName,
            dateAdded: product.createdAt,
            discount: calculateDiscount(
              product.actualPrice,
              product.sellingPrice
            ),
            stock: product.stock,
            availability: product.availability,
          }));

        setNewProducts(transformedProducts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const calculateDiscount = (actualPrice, sellingPrice) => {
    if (!actualPrice || !sellingPrice) return 0;
    const discount = ((actualPrice - sellingPrice) / actualPrice) * 100;
    return Math.round(discount);
  };

  const handleFilterChange = (filterType, value) => {
    setActiveFilters((prev) => {
      const updatedFilters = { ...prev };
      if (updatedFilters[filterType].includes(value)) {
        updatedFilters[filterType] = updatedFilters[filterType].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterType] = [...updatedFilters[filterType], value];
      }
      return updatedFilters;
    });
  };

  const getSortedProducts = (products) => {
    switch (sortBy) {
      case "price-low-high":
        return [...products].sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^0-9]/g, ""));
          const priceB = parseInt(b.price.replace(/[^0-9]/g, ""));
          return priceA - priceB;
        });
      case "price-high-low":
        return [...products].sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^0-9]/g, ""));
          const priceB = parseInt(b.price.replace(/[^0-9]/g, ""));
          return priceB - priceA;
        });
      default:
        return products;
    }
  };

  const getFilteredProducts = (products) => {
    return products.filter((product) => {
      // Price Range Filter
      const productPrice = parseInt(product.price.replace(/[^0-9]/g, ""));
      if (
        productPrice < activeFilters.priceRange.min ||
        productPrice > activeFilters.priceRange.max
      ) {
        return false;
      }

      // Material Filter
      if (
        activeFilters.materialAndCare.length > 0 &&
        !activeFilters.materialAndCare.some((material) =>
          product.fabric.toLowerCase().includes(material.toLowerCase())
        )
      ) {
        return false;
      }

      // Color Filter
      if (
        activeFilters.color.length > 0 &&
        !activeFilters.color.includes(product.color)
      ) {
        return false;
      }

      // Size Filter
      if (activeFilters.size.length > 0) {
        const productSize = product.size.toLowerCase();
        if (
          !activeFilters.size.some((size) =>
            productSize.includes(size.toLowerCase())
          )
        ) {
          return false;
        }
      }

      // Category Filter
      if (
        activeFilters.categoryName.length > 0 &&
        !activeFilters.categoryName.includes(product.type)
      ) {
        return false;
      }

      // Discount Filter
      if (activeFilters.discount.length > 0) {
        const productDiscount = product.discount;
        if (
          !activeFilters.discount.some((discountRange) => {
            const minDiscount = parseInt(discountRange);
            return productDiscount >= minDiscount;
          })
        ) {
          return false;
        }
      }

      return true;
    });
  };

  const getAvailableFilterOptions = (products) => {
    const options = {
      materialAndCare: new Set(),
      color: new Set(),
      categoryName: new Set(),
      size: new Set(),
    };

    products.forEach((product) => {
      if (product.fabric) options.materialAndCare.add(product.fabric);
      if (product.color) options.color.add(product.color);
      if (product.type) options.categoryName.add(product.type);
      if (product.size) options.size.add(product.size);
    });

    return {
      materialAndCare: Array.from(options.materialAndCare).filter(Boolean),
      color: Array.from(options.color).filter(Boolean),
      categoryName: Array.from(options.categoryName).filter(Boolean),
      size: Array.from(options.size).filter(Boolean),
    };
  };

  const getFilteredProductsCount = (min, max) => {
    return newProducts.filter((product) => {
      const price = parseInt(product.price.replace(/[^0-9]/g, ""));
      return (
        price >= min && price <= max && product.stock > 0 && !product.outOfStock
      );
    }).length;
  };

  const getDiscountedProductsCount = (minDiscount) => {
    return newProducts.filter((product) => {
      return (
        product.discount >= minDiscount &&
        product.stock > 0 &&
        !product.outOfStock
      );
    }).length;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="category-products">
      <div className="breadcrumb">
        <Link to="/">Home</Link> / New Collections
      </div>

      <button
        className="mobile-filter-button"
        onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
      >
        <i className="fas fa-filter"></i> Filters
      </button>

      <div
        className={`mobile-filter-overlay ${
          isMobileFilterOpen ? "active" : ""
        }`}
        onClick={() => setIsMobileFilterOpen(false)}
      ></div>

      <div className="filters-and-products">
        <div className={`filters ${isMobileFilterOpen ? "active" : ""}`}>
          <button
            className="close-filter-button"
            onClick={() => setIsMobileFilterOpen(false)}
          >
            ×
          </button>
          <h3>FILTER BY</h3>

          {/* Price Filter */}
          <div className="filter-section">
            <h4
              onClick={() =>
                setOpenFilter(openFilter === "price" ? null : "price")
              }
            >
              PRICE
              <span className="toggle-icon">
                {openFilter === "price" ? "−" : "+"}
              </span>
            </h4>
            {openFilter === "price" && (
              <div className="price-range-container">
                <div className="price-inputs">
                  <div className="input-group">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      placeholder="Min"
                      value={activeFilters.priceRange.min}
                      onChange={(e) => {
                        const value = Math.max(
                          0,
                          parseInt(e.target.value) || 0
                        );
                        setActiveFilters((prev) => ({
                          ...prev,
                          priceRange: {
                            ...prev.priceRange,
                            min: value,
                            max: Math.max(value, prev.priceRange.max),
                          },
                        }));
                      }}
                    />
                  </div>
                  <span className="separator">to</span>
                  <div className="input-group">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={activeFilters.priceRange.max}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setActiveFilters((prev) => ({
                          ...prev,
                          priceRange: {
                            ...prev.priceRange,
                            max: Math.max(value, prev.priceRange.min),
                          },
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="price-presets">
                  <button
                    className={
                      activeFilters.priceRange.max === 50 &&
                      activeFilters.priceRange.min === 0
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 0, max: 50 },
                      }))
                    }
                  >
                    Under $50 ({getFilteredProductsCount(0, 50)} items)
                  </button>
                  <button
                    className={
                      activeFilters.priceRange.max === 100 &&
                      activeFilters.priceRange.min === 50
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 50, max: 100 },
                      }))
                    }
                  >
                    $50 - $100 ({getFilteredProductsCount(50, 100)} items)
                  </button>
                  <button
                    className={
                      activeFilters.priceRange.min === 100 ? "active" : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 100, max: 1000 },
                      }))
                    }
                  >
                    Above $100 ({getFilteredProductsCount(100, 1000)} items)
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Material Filter */}
          {getAvailableFilterOptions(newProducts).materialAndCare.length >
            0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "material" ? null : "material")
                }
              >
                MATERIAL
                <span className="toggle-icon">
                  {openFilter === "material" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "material" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(newProducts).materialAndCare.map(
                    (material) => (
                      <label key={material} className="filter-option">
                        <input
                          type="checkbox"
                          checked={activeFilters.materialAndCare.includes(
                            material
                          )}
                          onChange={() =>
                            handleFilterChange("materialAndCare", material)
                          }
                        />
                        {material}
                      </label>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {/* Color Filter */}
          {getAvailableFilterOptions(newProducts).color.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "color" ? null : "color")
                }
              >
                COLOR
                <span className="toggle-icon">
                  {openFilter === "color" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "color" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(newProducts).color.map((color) => (
                    <label key={color} className="filter-option">
                      <input
                        type="checkbox"
                        checked={activeFilters.color.includes(color)}
                        onChange={() => handleFilterChange("color", color)}
                      />
                      {color}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Size Filter */}
          {getAvailableFilterOptions(newProducts).size.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "size" ? null : "size")
                }
              >
                SIZE
                <span className="toggle-icon">
                  {openFilter === "size" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "size" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(newProducts).size.map((size) => (
                    <label key={size} className="filter-option">
                      <input
                        type="checkbox"
                        checked={activeFilters.size.includes(size)}
                        onChange={() => handleFilterChange("size", size)}
                      />
                      {size}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Category Filter */}
          {getAvailableFilterOptions(newProducts).categoryName.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "category" ? null : "category")
                }
              >
                CATEGORY
                <span className="toggle-icon">
                  {openFilter === "category" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "category" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(newProducts).categoryName.map(
                    (category) => (
                      <label key={category} className="filter-option">
                        <input
                          type="checkbox"
                          checked={activeFilters.categoryName.includes(
                            category
                          )}
                          onChange={() =>
                            handleFilterChange("categoryName", category)
                          }
                        />
                        {category}
                      </label>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {/* Discount Filter */}
          {newProducts.some((product) => product.discount > 0) && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "discount" ? null : "discount")
                }
              >
                DISCOUNT
                <span className="toggle-icon">
                  {openFilter === "discount" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "discount" && (
                <div className="filter-options">
                  {["10", "20", "30", "40", "50"].map((discount) => {
                    const count = getDiscountedProductsCount(
                      parseInt(discount)
                    );
                    if (count > 0) {
                      return (
                        <label key={discount} className="filter-option">
                          <input
                            type="checkbox"
                            checked={activeFilters.discount.includes(discount)}
                            onChange={() =>
                              handleFilterChange("discount", discount)
                            }
                          />
                          <span className="discount-label">
                            {discount}% and above
                            <span className="discount-count">({count})</span>
                          </span>
                        </label>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="results-sort-section">
            <div className="results-count">
              {
                getSortedProducts(getFilteredProducts(newProducts)).filter(
                  (product) => product.stock > 0 && !product.outOfStock
                ).length
              }{" "}
              {getSortedProducts(getFilteredProducts(newProducts)).filter(
                (product) => product.stock > 0 && !product.outOfStock
              ).length === 1
                ? "Result"
                : "Results"}
            </div>

            <div className="sort-section">
              <label>SORT BY : </label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="relevance">Relevance</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
              </select>
            </div>
          </div>

          <div className="products-grid">
            {getSortedProducts(getFilteredProducts(newProducts))
              .filter((product) => product.stock > 0 && !product.outOfStock)
              .map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCollections;
